import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import { ShortcutHint, SHIFT_SHORTCUT } from '@/components/shortcuts';
import { useShift } from './useShift';
import { shiftGateNotice } from './shiftMeta';

/**
 * The cart's line on why selling is held while this seller has no shift open:
 * the prompt to open one, or, from the sale point handover guard (SEL-30),
 * whose shift still holds the drawer and when it opened. While the seller's
 * own status is still being read it says so, and when that read or the
 * handover check failed it offers a retry (SEL-49; `shiftGateNotice` picks
 * which). Shows the `F10` hint that opens the shift dialog (bos-seller#60),
 * in place even while `F10` waits for the status. Renders nothing while a
 * shift is open.
 */
export const ShiftGateNotice: React.FC = () => {
    const { t } = useTranslation('pages');
    const { isOpen, statusRead, handover } = useShift();

    const notice = shiftGateNotice({ statusRead, isOpen, handover });
    if (!notice) {
        return null;
    }

    return (
        <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-1.5">
                <Typography
                    className="text-center text-warning"
                    {...getNodeTestIdProp('seller-shift-gate-notice')}
                    type="body-xs"
                >
                    {t(notice.key, notice.values)}
                </Typography>
                <ShortcutHint shortcut={SHIFT_SHORTCUT} />
            </div>
            {notice.onRetry ? (
                <Button
                    {...getNodeTestIdProp('seller-shift-gate-retry')}
                    onPress={notice.onRetry}
                    size="sm"
                    variant="ghost"
                >
                    {t('seller.shift.retryCheck')}
                </Button>
            ) : null}
        </div>
    );
};
